'use client';

import { appendCampaignParamsToUrl } from '@/lib/persistCampaignParams';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import type { ComponentProps } from 'react';
import { forwardRef, useLayoutEffect, useState } from 'react';

/**
 * UTM / kampanya parametreleri sunucuda bilinmez; href’e doğrudan append etmek hydration
 * uyumsuzluğu üretir. İlk paint sunucu ile aynı kalır, mount sonrası query eklenir.
 *
 * Default prefetch={false}: homepage/footer viewport prefetch used to fire 30–40
 * origin RSC flights and stall the click the user actually made. Hover/focus/touch
 * still warms the Next router cache for the link they are about to open.
 */
export type CampaignLinkProps = Omit<ComponentProps<typeof Link>, 'href'> & {
  href: string;
};

function isInternalAppPath(href: string) {
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return false;
  }
  if (href.startsWith('http://') || href.startsWith('https://')) {
    try {
      return new URL(href).origin === window.location.origin;
    } catch {
      return false;
    }
  }
  return href.startsWith('/');
}

export const CampaignLink = forwardRef<HTMLAnchorElement, CampaignLinkProps>(function CampaignLink(
  { href, prefetch = false, onMouseEnter, onFocus, onTouchStart, ...rest },
  ref,
) {
  const [resolved, setResolved] = useState(href);
  const router = useRouter();

  useLayoutEffect(() => {
    setResolved(appendCampaignParamsToUrl(href));
  }, [href]);

  const prefetchOnIntent = () => {
    if (prefetch) return;
    if (!isInternalAppPath(resolved)) return;
    try {
      router.prefetch(resolved);
    } catch {
      // ignore
    }
  };

  return (
    <Link
      ref={ref}
      href={resolved}
      prefetch={prefetch}
      {...rest}
      onMouseEnter={(event) => {
        prefetchOnIntent();
        onMouseEnter?.(event);
      }}
      onFocus={(event) => {
        prefetchOnIntent();
        onFocus?.(event);
      }}
      onTouchStart={(event) => {
        prefetchOnIntent();
        onTouchStart?.(event);
      }}
    />
  );
});
